import { useEffect, useMemo, useState } from "react";
import { Button, Switch, message } from "antd";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import YextApi, { yextErrorMessage } from "../../api/YextApi";
import "./BusinessHoursEditor.scss";

type DayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

interface TimeRange {
  // Stored as minutes from midnight (0–1439). Display formatting depends on AM/PM vs 24h mode.
  start: number;
  end: number;
}

interface DayHours {
  open: boolean;
  ranges: TimeRange[];
}

const DAYS: { key: DayKey; label: string }[] = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

const DEFAULT_RANGE: TimeRange = { start: 8 * 60, end: 21 * 60 };

function defaultHours(): Record<DayKey, DayHours> {
  return {
    mon: { open: false, ranges: [{ ...DEFAULT_RANGE }] },
    tue: { open: true, ranges: [{ ...DEFAULT_RANGE }] },
    wed: { open: true, ranges: [{ ...DEFAULT_RANGE }] },
    thu: { open: true, ranges: [{ ...DEFAULT_RANGE }] },
    fri: { open: true, ranges: [{ ...DEFAULT_RANGE }] },
    sat: { open: true, ranges: [{ ...DEFAULT_RANGE }] },
    sun: { open: true, ranges: [{ ...DEFAULT_RANGE }] },
  };
}

interface Props {
  locationId?: number;
}

export function BusinessHoursEditor({ locationId }: Props) {
  const [hours, setHours] = useState<Record<DayKey, DayHours>>(defaultHours);
  const [use24h, setUse24h] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!locationId) return;
    YextApi.getProfile(locationId).then(({ data }) => {
      if (!data.hours) return;
      // Overlay stored days onto the defaults so any missing day still renders.
      setHours((current) => ({ ...current, ...(data.hours as Record<DayKey, DayHours>) }));
    });
  }, [locationId]);

  async function saveHours() {
    if (!locationId) {
      message.error("No location selected.");
      return;
    }
    setSaving(true);
    try {
      // The hours shape (mon..sun with open + minute ranges) matches the backend contract as-is.
      await YextApi.syncLocation(locationId, { hours });
      message.success("Business hours saved.");
    } catch (e) {
      message.error(yextErrorMessage(e, "Failed to save business hours."));
    } finally {
      setSaving(false);
    }
  }

  function updateDay(day: DayKey, next: DayHours) {
    setHours((h) => ({ ...h, [day]: next }));
  }

  function toggleOpen(day: DayKey, open: boolean) {
    updateDay(day, { ...hours[day], open });
  }

  function updateRange(day: DayKey, idx: number, range: TimeRange) {
    const ranges = hours[day].ranges.map((r, i) => (i === idx ? range : r));
    updateDay(day, { ...hours[day], ranges });
  }

  function addRange(day: DayKey) {
    const ranges = [...hours[day].ranges, { ...DEFAULT_RANGE }];
    updateDay(day, { ...hours[day], ranges });
  }

  function removeRange(day: DayKey, idx: number) {
    const ranges = hours[day].ranges.filter((_, i) => i !== idx);
    if (ranges.length === 0) ranges.push({ ...DEFAULT_RANGE });
    updateDay(day, { ...hours[day], ranges });
  }

  return (
    <section className="business-hours-editor">
      <header className="bh-header">
        <h3>Business Hours</h3>
        <div className="bh-format-toggle">
          <span className={!use24h ? "active" : ""}>AM/PM</span>
          <Switch checked={use24h} onChange={setUse24h} />
          <span className={use24h ? "active" : ""}>24h</span>
        </div>
      </header>

      <div className="bh-rows">
        {DAYS.map(({ key, label }) => (
          <DayRow
            key={key}
            label={label}
            day={hours[key]}
            use24h={use24h}
            onToggleOpen={(open) => toggleOpen(key, open)}
            onChangeRange={(idx, range) => updateRange(key, idx, range)}
            onAddRange={() => addRange(key)}
            onRemoveRange={(idx) => removeRange(key, idx)}
          />
        ))}
      </div>

      <footer className="bh-actions">
        <Button type="primary" danger size="large" onClick={saveHours} loading={saving}>
          Save Hours
        </Button>
      </footer>
    </section>
  );
}

interface DayRowProps {
  label: string;
  day: DayHours;
  use24h: boolean;
  onToggleOpen: (open: boolean) => void;
  onChangeRange: (idx: number, range: TimeRange) => void;
  onAddRange: () => void;
  onRemoveRange: (idx: number) => void;
}

function DayRow({
  label,
  day,
  use24h,
  onToggleOpen,
  onChangeRange,
  onAddRange,
  onRemoveRange,
}: DayRowProps) {
  return (
    <div className={`bh-row${day.open ? "" : " closed"}`}>
      <div className="bh-day-label">{label}</div>
      <div className="bh-toggle-cell">
        <Switch checked={day.open} onChange={onToggleOpen} />
        <span className="bh-state-label">{day.open ? "Open" : "Closed"}</span>
      </div>

      {day.open ? (
        <div className="bh-ranges">
          {day.ranges.map((range, idx) => (
            <div className="bh-range" key={idx}>
              <TimeField
                value={range.start}
                use24h={use24h}
                onChange={(v) => onChangeRange(idx, { ...range, start: v })}
              />
              <span className="bh-dash">—</span>
              <TimeField
                value={range.end}
                use24h={use24h}
                onChange={(v) => onChangeRange(idx, { ...range, end: v })}
              />
              {day.ranges.length > 1 && (
                <button
                  type="button"
                  className="bh-remove-btn"
                  aria-label="Remove time range"
                  onClick={() => onRemoveRange(idx)}
                >
                  <CloseOutlined />
                </button>
              )}
              {idx === day.ranges.length - 1 && (
                <button
                  type="button"
                  className="bh-add-btn"
                  aria-label="Add time range"
                  onClick={onAddRange}
                >
                  <PlusOutlined />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bh-ranges bh-ranges-disabled" aria-hidden />
      )}
    </div>
  );
}

interface TimeFieldProps {
  value: number; // minutes from midnight
  use24h: boolean;
  onChange: (value: number) => void;
}

function TimeField({ value, use24h, onChange }: TimeFieldProps) {
  const hours24 = Math.floor(value / 60);
  const minutes = value % 60;
  const isPm = hours24 >= 12;
  const hours12Raw = hours24 % 12;
  const hours12 = hours12Raw === 0 ? 12 : hours12Raw;

  const displayValue = useMemo(() => {
    if (use24h) {
      return `${pad(hours24)}:${pad(minutes)}`;
    }
    return `${hours12}:${pad(minutes)}`;
  }, [use24h, hours24, hours12, minutes]);

  function setMinutesFromInput(text: string) {
    const m = text.match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return;
    const h = parseInt(m[1], 10);
    const mi = parseInt(m[2], 10);
    if (isNaN(h) || isNaN(mi) || mi < 0 || mi > 59) return;
    if (use24h) {
      if (h < 0 || h > 23) return;
      onChange(h * 60 + mi);
    } else {
      if (h < 1 || h > 12) return;
      const h24 = (h % 12) + (isPm ? 12 : 0);
      onChange(h24 * 60 + mi);
    }
  }

  function setMeridiem(next: "AM" | "PM") {
    const wantPm = next === "PM";
    if (wantPm === isPm) return;
    const h12 = hours12Raw === 0 ? 12 : hours12Raw;
    const h24 = (h12 % 12) + (wantPm ? 12 : 0);
    onChange(h24 * 60 + minutes);
  }

  return (
    <div className="bh-time-field">
      <input
        type="text"
        className="bh-time-input"
        value={displayValue}
        onChange={(e) => setMinutesFromInput(e.target.value)}
        spellCheck={false}
      />
      {!use24h && (
        <select
          className="bh-meridiem"
          value={isPm ? "PM" : "AM"}
          onChange={(e) => setMeridiem(e.target.value as "AM" | "PM")}
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      )}
    </div>
  );
}

function pad(n: number) {
  return n < 10 ? `0${n}` : `${n}`;
}
